import type { LengthUnit } from '@fulcrum/lib';
import { convertLength } from '@trimble/utilities';
import { hexToRgb, rgbToHex } from '@trimble/ui-components/solidjs';
import {
  formatMeterValueForStorage,
  parseStopValue,
} from '@trimble/measurements/volume-heatmap-palette';
import type { TextureType } from './types/heatmap.types';

export const FULL_PERCENT = 100;
export const OVERFLOW_GREY = '#5c5c5c';
export const STOP_ROW_HEIGHT_PX = 40;
export const GRADIENT_STOP_BAR_WIDTH_PX = 44;
export const GRADIENT_STOP_COLUMN_WIDTH_PX = 44;
export const GRADIENT_STOP_BAND_OFFSET_PX = 6;
export const COLOR_SWATCH_SIZE_PX = 24;
export const COLOR_SWATCH_WRAPPER_SIZE_PX = 28;
export const COLOR_SWATCH_PADDING_PX = 2;
export const COLOR_SWATCH_BORDER_RADIUS_PX = 4;
export const OVERFLOW_CAP_WIDTH_PERCENT = 10;

/** Top edge of a swatch when it is vertically centred in a stop row slot. */
export function stopRowSwatchTopPx(rowTopPx: number): number {
  return (
    rowTopPx +
    (STOP_ROW_HEIGHT_PX - COLOR_SWATCH_WRAPPER_SIZE_PX) / HALF_DIVISOR
  );
}

/** Vertical centre of a swatch centred in a stop row slot. */
export function stopRowSwatchCenterTopPx(rowTopPx: number): number {
  return (
    stopRowSwatchTopPx(rowTopPx) + COLOR_SWATCH_WRAPPER_SIZE_PX / HALF_DIVISOR
  );
}

const HALF_DIVISOR = 2;
const HALF_PERCENT = FULL_PERCENT / HALF_DIVISOR;
const BOUND_VALUE_DECIMAL_PLACES = 3;
/** Above + below overflow rows in the stop list column. */
const OVERFLOW_CAP_ROW_COUNT = 2;

/** Minimal stop shape (id, colour, value) used by the bar-paint helpers. */
type StopColorValue = {
  readonly id: string;
  readonly color: string;
  readonly value: string;
};

/** Options for aligning stop rows, handles, and editor bar paint. */
export interface StopLayoutOptions {
  readonly textureType: TextureType;
  /**
   * When true, enforces {@link STOP_ROW_HEIGHT_PX} minimum spacing between rows
   * so clustered stops stay readable (stop list and gradient bar in both edit
   * and read-only modes). When false, positions follow value-proportional
   * ideal positions ({@link VIEWER_STOP_LAYOUT}).
   */
  readonly clampOverlappingRows: boolean;
}

/** Value-proportional layout for helpers that still paint by stop value. */
export const VIEWER_STOP_LAYOUT: Omit<StopLayoutOptions, 'textureType'> = {
  clampOverlappingRows: false,
};

/** Clamped row layout for stop lists and the gradient bar (edit and read-only). */
export const EDITABLE_STOP_LAYOUT: Omit<StopLayoutOptions, 'textureType'> = {
  clampOverlappingRows: true,
};

function resolveStopLayoutOptions(
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): StopLayoutOptions {
  return {
    textureType,
    clampOverlappingRows:
      options?.clampOverlappingRows ??
      EDITABLE_STOP_LAYOUT.clampOverlappingRows,
  };
}

export interface StopValueRange {
  readonly min: number;
  readonly max: number;
}

export function stopValueRange(
  stops: readonly { value: string }[]
): StopValueRange {
  const values = stops.map((stop) => parseStopValue(stop.value));
  if (values.length === 0) {
    return { min: 0, max: 0 };
  }
  return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * Value at the centre of the largest gap between distinct stop values
 * (descending order). Used for keyboard bar insert so activation does not
 * target the range midpoint when that value already exists.
 */
export function largestGapMidpointValueInMeters(
  stops: readonly { value: string }[]
): number {
  const uniqueDesc = [
    ...new Set(stops.map((stop) => parseStopValue(stop.value))),
  ].sort((left, right) => right - left);

  if (uniqueDesc.length === 0) {
    return 0;
  }
  if (uniqueDesc.length === 1) {
    return uniqueDesc[0];
  }

  let bestGap = -1;
  let bestMidpoint =
    (uniqueDesc[0] + uniqueDesc[uniqueDesc.length - 1]) / HALF_DIVISOR;

  for (let index = 0; index < uniqueDesc.length - 1; index++) {
    const upper = uniqueDesc[index];
    const lower = uniqueDesc[index + 1];
    const gap = upper - lower;
    if (gap > bestGap) {
      bestGap = gap;
      bestMidpoint = (upper + lower) / HALF_DIVISOR;
    }
  }

  return bestMidpoint;
}

/** Maps a stop value to a vertical percent (0 = top, 100 = bottom). Higher values sit toward the top. */
export function valueToVerticalPercent(
  value: number,
  range: StopValueRange
): number {
  if (range.max === range.min) {
    return HALF_PERCENT;
  }
  return ((range.max - value) / (range.max - range.min)) * FULL_PERCENT;
}

/** Inverse of {@link valueToVerticalPercent} — maps vertical percent back to a stop value. */
export function verticalPercentToValue(
  verticalPercent: number,
  range: StopValueRange
): number {
  if (range.max === range.min) {
    return range.max;
  }
  return range.max - (verticalPercent / FULL_PERCENT) * (range.max - range.min);
}

interface BarLayoutValueAnchor {
  readonly valueInMeters: number;
  readonly anchorTopPx: number;
}

/** Editable-bar colour anchors (clamped / equal-slot), top to bottom. */
function barEditableLayoutValueAnchors(
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType
): readonly BarLayoutValueAnchor[] {
  const sortedStops = sortStopsByValueDescending(stopsDesc);
  const layout = resolveStopLayoutOptions(textureType, EDITABLE_STOP_LAYOUT);
  return sortedStops.map((stop) => ({
    valueInMeters: parseStopValue(stop.value),
    anchorTopPx: stopBarColorAnchorTopPx(stop, sortedStops, layout),
  }));
}

function barEditableLayoutValueAnchorsSorted(
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType
): readonly BarLayoutValueAnchor[] {
  return [...barEditableLayoutValueAnchors(stopsDesc, textureType)].sort(
    (left, right) => left.anchorTopPx - right.anchorTopPx
  );
}

function interpolateValueAlongAnchors(
  from: BarLayoutValueAnchor,
  to: BarLayoutValueAnchor,
  anchorTopPx: number
): number {
  const spanPx = to.anchorTopPx - from.anchorTopPx;
  if (spanPx === 0) {
    return from.valueInMeters;
  }
  const t = (anchorTopPx - from.anchorTopPx) / spanPx;
  return from.valueInMeters + t * (to.valueInMeters - from.valueInMeters);
}

function interpolateAnchorAlongValues(
  from: BarLayoutValueAnchor,
  to: BarLayoutValueAnchor,
  valueInMeters: number
): number {
  const spanValue = to.valueInMeters - from.valueInMeters;
  if (spanValue === 0) {
    return from.anchorTopPx;
  }
  const t = (valueInMeters - from.valueInMeters) / spanValue;
  return from.anchorTopPx + t * (to.anchorTopPx - from.anchorTopPx);
}

function segmentExtendingAboveFirst(
  anchors: readonly BarLayoutValueAnchor[]
): readonly [BarLayoutValueAnchor, BarLayoutValueAnchor] | undefined {
  const first = anchors[0];
  for (let index = 1; index < anchors.length; index++) {
    if (anchors[index].valueInMeters !== first.valueInMeters) {
      return [first, anchors[index]];
    }
  }
  return undefined;
}

function segmentExtendingBelowLast(
  anchors: readonly BarLayoutValueAnchor[]
): readonly [BarLayoutValueAnchor, BarLayoutValueAnchor] | undefined {
  const last = anchors[anchors.length - 1];
  for (let index = anchors.length - HALF_DIVISOR; index >= 0; index--) {
    if (anchors[index].valueInMeters !== last.valueInMeters) {
      return [anchors[index], last];
    }
  }
  return undefined;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 16 > 15 allowed; refactor later
function barAnchorTopPxToValueInMeters(
  anchorTopPx: number,
  anchors: readonly BarLayoutValueAnchor[]
): number {
  if (anchors.length === 0) {
    return 0;
  }
  if (anchors.length === 1) {
    return anchors[0].valueInMeters;
  }

  const first = anchors[0];
  const last = anchors[anchors.length - 1];

  if (anchorTopPx <= first.anchorTopPx) {
    if (anchorTopPx === first.anchorTopPx) {
      return first.valueInMeters;
    }
    const segment = segmentExtendingAboveFirst(anchors);
    return segment === undefined
      ? first.valueInMeters
      : interpolateValueAlongAnchors(segment[0], segment[1], anchorTopPx);
  }
  if (anchorTopPx >= last.anchorTopPx) {
    if (anchorTopPx === last.anchorTopPx) {
      return last.valueInMeters;
    }
    const segment = segmentExtendingBelowLast(anchors);
    return segment === undefined
      ? last.valueInMeters
      : interpolateValueAlongAnchors(segment[0], segment[1], anchorTopPx);
  }

  for (let index = 0; index < anchors.length - 1; index++) {
    const from = anchors[index];
    const to = anchors[index + 1];
    if (anchorTopPx >= from.anchorTopPx && anchorTopPx <= to.anchorTopPx) {
      return interpolateValueAlongAnchors(from, to, anchorTopPx);
    }
  }

  return last.valueInMeters;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 17 > 15 allowed; refactor later
function valueInMetersToBarAnchorTopPx(
  valueInMeters: number,
  anchors: readonly BarLayoutValueAnchor[]
): number {
  if (anchors.length === 0) {
    return 0;
  }
  if (anchors.length === 1) {
    return anchors[0].anchorTopPx;
  }

  const first = anchors[0];
  const last = anchors[anchors.length - 1];
  if (first.valueInMeters === last.valueInMeters) {
    return (first.anchorTopPx + last.anchorTopPx) / HALF_DIVISOR;
  }

  if (valueInMeters > first.valueInMeters) {
    const segment = segmentExtendingAboveFirst(anchors);
    return segment === undefined
      ? first.anchorTopPx
      : interpolateAnchorAlongValues(segment[0], segment[1], valueInMeters);
  }
  if (valueInMeters === first.valueInMeters) {
    return first.anchorTopPx;
  }
  if (valueInMeters < last.valueInMeters) {
    const segment = segmentExtendingBelowLast(anchors);
    return segment === undefined
      ? last.anchorTopPx
      : interpolateAnchorAlongValues(segment[0], segment[1], valueInMeters);
  }
  if (valueInMeters === last.valueInMeters) {
    return last.anchorTopPx;
  }

  for (let index = 0; index < anchors.length - 1; index++) {
    const from = anchors[index];
    const to = anchors[index + 1];
    if (from.valueInMeters === to.valueInMeters) {
      continue;
    }
    if (
      valueInMeters <= from.valueInMeters &&
      valueInMeters >= to.valueInMeters
    ) {
      return interpolateAnchorAlongValues(from, to, valueInMeters);
    }
  }

  return last.anchorTopPx;
}

/**
 * Maps a swatch-centre anchor Y (track-relative px) to a stop value in metres.
 * Shared by bar click insert and handle drag preview so both follow the
 * editable bar's clamped colour anchors.
 */
export function barTrackOffsetYToValueInMeters(
  anchorTopPx: number,
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType = 'gradient'
): number {
  return barAnchorTopPxToValueInMeters(
    anchorTopPx,
    barEditableLayoutValueAnchorsSorted(stopsDesc, textureType)
  );
}

/**
 * Maps a handle wrapper top on the gradient bar to a stop value in metres.
 * Inverse of {@link valueInMetersToBarHandleTopPx}. Uses the editable bar's
 * clamped colour anchors so a pointer on a handle round-trips to that stop's
 * value even when clustered rows are equal-spaced. Overflow is extrapolated
 * along the nearest value-span segment.
 */
export function barOffsetYToValueInMeters(
  handleTopPx: number,
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType = 'gradient'
): number {
  return barTrackOffsetYToValueInMeters(
    handleTopPx + GRADIENT_STOP_BAND_OFFSET_PX,
    stopsDesc,
    textureType
  );
}

/**
 * Maps a bar-track click Y (track-relative px) to a stop value in metres.
 * Interpolates along the same clamped anchors used by
 * {@link heatmapEditorBarPixelCss} in edit mode. Overflow-cap clicks
 * extrapolate beyond the current min/max so insert can extend the range.
 */
export function barPickOffsetYToValueInMeters(
  offsetY: number,
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType = 'gradient'
): number {
  const totalHeightPx = stopListHeightPx(stopsDesc.length);
  const clampedY = Math.min(Math.max(offsetY, 0), totalHeightPx);
  return barTrackOffsetYToValueInMeters(clampedY, stopsDesc, textureType);
}

/** Maps a stop value in metres to the handle wrapper top px on the gradient bar. */
export function valueInMetersToBarHandleTopPx(
  valueInMeters: number,
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType = 'gradient'
): number {
  return (
    valueInMetersToBarAnchorTopPx(
      valueInMeters,
      barEditableLayoutValueAnchorsSorted(stopsDesc, textureType)
    ) - GRADIENT_STOP_BAND_OFFSET_PX
  );
}

/** Sort boundary — use at load, add, and value commit. Stable among equal values. */
export function sortStopsByValueDescending<T extends { value: string }>(
  stops: readonly T[]
): T[] {
  return stops.toSorted(
    (a, b) => parseStopValue(b.value) - parseStopValue(a.value)
  );
}

function stopsAscFromDesc<T extends { value: string }>(
  stopsDesc: readonly T[]
): readonly T[] {
  return [...stopsDesc].reverse();
}

/**
 * Stop layout and bar-paint helpers below that take `stopsDesc` assume
 * value-descending order (canonical store / API order). The mapper preserves
 * API order and warns on contract violations; sort at add and value-commit
 * boundaries via {@link sortStopsByValueDescending}.
 */

export interface OverflowColorOverrides {
  readonly above?: string | null;
  readonly below?: string | null;
}

export function overflowStopColors(
  stops: readonly { color: string; value: string }[],
  overrides?: OverflowColorOverrides,
  textureType?: TextureType
): { above: string; below: string } {
  const sorted = sortStopsByValueDescending(stops);
  // Stepped mirrors the tile renderer: the lowest stop owns every value at or below its
  // own value, so there is no separate out-of-range-below colour. The below override is
  // ignored for stepped and the lowest stop colour fills the tail. Gradient keeps its
  // out-of-range-below override, and the above cap is unchanged for both textures.
  const belowOverride =
    textureType === 'stepped' ? undefined : overrides?.below;
  return {
    above: overrides?.above ?? sorted[0]?.color ?? OVERFLOW_GREY,
    below: belowOverride ?? sorted.at(-1)?.color ?? OVERFLOW_GREY,
  };
}

export function formatOverflowLabel(
  bound: 'above' | 'below',
  boundValue: number,
  unit: string
): string {
  const prefix = bound === 'above' ? '>' : '<';
  return `${prefix} ${formatBoundValue(boundValue)} ${unit}`;
}

function formatBoundValue(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  // eslint-disable-next-line sonarjs/super-linear-regex -- input is a short toFixed() result
  return value.toFixed(BOUND_VALUE_DECIMAL_PLACES).replace(/\.?0+$/, '');
}

function roundToBoundPrecision(value: number): number {
  return parseFloat(value.toFixed(BOUND_VALUE_DECIMAL_PLACES));
}

/**
 * Convert a stop value from the canonical store unit (metres) into the active
 * display unit. Stops are stored in metres to satisfy the measurements API's
 * strict-descending contract, so this conversion is display-only
 */
export function convertStopValueToDisplay(
  valueInMeters: number,
  displayUnit: LengthUnit
): number {
  return displayUnit === 'm'
    ? valueInMeters
    : convertLength(valueInMeters, 'm', displayUnit);
}

/**
 * Format a stored (metre) stop value string for display in the active unit.
 * Metric projects keep the authored string as-is (preserving trailing zeros);
 * other units are converted and formatted to {@link BOUND_VALUE_DECIMAL_PLACES}.
 */
export function formatStopValueForDisplay(
  valueInMeters: string,
  displayUnit: LengthUnit
): string {
  if (displayUnit === 'm') {
    return valueInMeters;
  }
  return formatBoundValue(
    convertLength(parseStopValue(valueInMeters), 'm', displayUnit)
  );
}

/**
 * Format a canonical metre quantity for stop row display (e.g. while dragging a
 * handle). Uses the same bound precision as commit so the live value matches
 * what is saved. Non-metric projects see values in the project length unit.
 */
export function formatMeterQuantityForStopDisplay(
  valueInMeters: number,
  displayUnit: LengthUnit
): string {
  return formatStopValueForDisplay(
    formatStopValueInMeters(valueInMeters, displayUnit),
    displayUnit
  );
}

/**
 * Format a canonical metre value for persistence on an EditableStop.
 * Metric projects round in metres; other units round in the project display unit
 * first so values like 2 ft do not become 0.610 m and display as 2.001 ft.
 */
export function formatStopValueInMeters(
  valueInMeters: number,
  displayUnit: LengthUnit
): string {
  if (displayUnit === 'm') {
    return formatBoundValue(valueInMeters);
  }

  const roundedDisplay = roundToBoundPrecision(
    convertLength(valueInMeters, 'm', displayUnit)
  );
  const canonicalMeters = convertLength(roundedDisplay, displayUnit, 'm');
  return formatMeterValueForStorage(canonicalMeters);
}

/** Display stop values are plain decimals (not scientific notation). */
const PLAIN_DECIMAL_STOP_DISPLAY_PATTERN = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;

/**
 * Parse a user-typed display value back into canonical metres, or `null` when the
 * input is empty, incomplete, or not numeric.
 */
export function parseDisplayStopValueToMeters(
  display: string,
  displayUnit: LengthUnit
): number | null {
  const trimmed = display.trim();
  if (trimmed === '' || trimmed === '-') return null;
  if (!PLAIN_DECIMAL_STOP_DISPLAY_PATTERN.test(trimmed)) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return displayUnit === 'm' ? parsed : convertLength(parsed, displayUnit, 'm');
}

/** Whether two stored metre strings represent the same stop in the active unit. */
export function stopValuesSemanticallyEqual(
  left: string,
  right: string,
  displayUnit: LengthUnit
): boolean {
  return (
    formatStopValueInMeters(parseStopValue(left), displayUnit) ===
    formatStopValueInMeters(parseStopValue(right), displayUnit)
  );
}

const RGB_RED_CHANNEL = 0;
const RGB_GREEN_CHANNEL = 1;
const RGB_BLUE_CHANNEL = 2;

function lerpRgb(
  from: [number, number, number],
  to: [number, number, number],
  t: number
): string {
  const mix = (a: number, b: number): number => Math.round(a + (b - a) * t);
  return rgbToHex([
    mix(from[RGB_RED_CHANNEL], to[RGB_RED_CHANNEL]),
    mix(from[RGB_GREEN_CHANNEL], to[RGB_GREEN_CHANNEL]),
    mix(from[RGB_BLUE_CHANNEL], to[RGB_BLUE_CHANNEL]),
  ]);
}

/**
 * Interpolate the gradient colour at `valueInMeters` between the two nearest
 * stops (RGB lerp). Values outside the stop range clamp to the endpoint colour.
 * @param stopsDesc Stops in value-descending order (canonical store order).
 */
export function colorAtValue(
  valueInMeters: number,
  stopsDesc: readonly { color: string; value: string }[]
): string {
  if (stopsDesc.length === 0) {
    return OVERFLOW_GREY;
  }
  if (stopsDesc.length === 1) {
    return stopsDesc[0].color;
  }

  const sorted = stopsAscFromDesc(stopsDesc);
  const minValue = parseStopValue(sorted[0].value);
  const maxValue = parseStopValue(sorted[sorted.length - 1].value);

  if (valueInMeters <= minValue) {
    return sorted[0].color;
  }
  if (valueInMeters >= maxValue) {
    return sorted[sorted.length - 1].color;
  }

  for (let index = 0; index < sorted.length - 1; index++) {
    const lowerValue = parseStopValue(sorted[index].value);
    const upperValue = parseStopValue(sorted[index + 1].value);
    if (valueInMeters >= lowerValue && valueInMeters <= upperValue) {
      if (upperValue === lowerValue) {
        return sorted[index].color;
      }
      const t = (valueInMeters - lowerValue) / (upperValue - lowerValue);
      return lerpRgb(
        hexToRgb(sorted[index].color),
        hexToRgb(sorted[index + 1].color),
        t
      );
    }
  }

  return sorted[sorted.length - 1].color;
}

/** Height of the overflow + stop list column that aligns with the gradient bar. */
export function stopListHeightPx(editableStopCount: number): number {
  return (editableStopCount + OVERFLOW_CAP_ROW_COUNT) * STOP_ROW_HEIGHT_PX;
}

/** Ideal row top for a stop before overlap clamping. */
export function stopRowIdealTopPx(
  stop: { id: string; value: string },
  stops: readonly { id: string; value: string }[]
): number {
  const range = stopValueRange(stops);
  const verticalPercent = valueToVerticalPercent(
    parseStopValue(stop.value),
    range
  );
  const { minTopPx, maxTopPx } = stopRowTopBoundsPx(stops.length);
  return minTopPx + (verticalPercent / FULL_PERCENT) * (maxTopPx - minTopPx);
}

/** Top offset of the overflow-above row (matches the bar overflow cap). */
export function overflowAboveRowTopPx(): number {
  return 0;
}

/** Top offset of the overflow-below row (matches the bar overflow cap). */
export function overflowBelowRowTopPx(editableStopCount: number): number {
  return stopListHeightPx(editableStopCount) - STOP_ROW_HEIGHT_PX;
}

/**
 * Editable stop row top range inside the list column — between overflow caps.
 */
export function stopRowTopBoundsPx(editableStopCount: number): {
  readonly minTopPx: number;
  readonly maxTopPx: number;
} {
  return {
    minTopPx: STOP_ROW_HEIGHT_PX,
    maxTopPx: overflowBelowRowTopPx(editableStopCount) - STOP_ROW_HEIGHT_PX,
  };
}

/**
 * Value-proportional row tops for handles, bar anchors, and value-column rows.
 */
function stopValueRowTopPxMap(
  stopsDesc: readonly { id: string; value: string }[],
  layout: StopLayoutOptions
): ReadonlyMap<string, number> {
  const { minTopPx, maxTopPx } = stopRowTopBoundsPx(stopsDesc.length);

  const equalSpacingRows = (): ReadonlyMap<string, number> =>
    new Map(
      stopsDesc.map((stop, index) => [
        stop.id,
        minTopPx + index * STOP_ROW_HEIGHT_PX,
      ])
    );

  const clampToBounds = (topPx: number): number =>
    Math.min(Math.max(topPx, minTopPx), maxTopPx);

  if (!layout.clampOverlappingRows) {
    return new Map(
      stopsDesc.map((stop) => [
        stop.id,
        clampToBounds(stopRowIdealTopPx(stop, stopsDesc)),
      ])
    );
  }

  const lowestStop = stopsDesc.at(-1);
  if (
    lowestStop !== undefined &&
    stopRowIdealTopPx(lowestStop, stopsDesc) > maxTopPx
  ) {
    return equalSpacingRows();
  }

  const topsById = new Map<string, number>();
  let previousTopPx = minTopPx - STOP_ROW_HEIGHT_PX;

  for (const stop of stopsDesc) {
    const topPx = Math.max(
      stopRowIdealTopPx(stop, stopsDesc),
      previousTopPx + STOP_ROW_HEIGHT_PX
    );
    if (topPx > maxTopPx) {
      return equalSpacingRows();
    }
    topsById.set(stop.id, topPx);
    previousTopPx = topPx;
  }

  return topsById;
}

/**
 * Row tops that follow handle layout but never overlap adjacent rows. When
 * handles are tighter than {@link STOP_ROW_HEIGHT_PX}, lower rows shift down.
 * Falls back to equal slot spacing if the ideal layout cannot fit above the
 * bottom overflow row.
 */
export function stopRowTopPxMap(
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): ReadonlyMap<string, number> {
  const layout = resolveStopLayoutOptions(textureType, options);
  return stopValueRowTopPxMap(stopsDesc, layout);
}

/** Row top for bar markers and handles. */
function stopValueLayoutRowTopPx(
  stop: { id: string; value: string },
  stopsDesc: readonly { id: string; value: string }[],
  layout: StopLayoutOptions
): number {
  const rowTop = stopValueRowTopPxMap(stopsDesc, layout).get(stop.id);
  if (rowTop !== undefined) {
    return rowTop;
  }
  return stopRowIdealTopPx(stop, stopsDesc);
}

/**
 * Pixel anchor on the editor bar for a stop (swatch vertical centre).
 * Stepped and gradient share the same marker positions; only bar paint differs.
 */
export function stopBarMarkerAnchorTopPx(
  stop: { id: string; value: string },
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): number {
  return stopBarColorAnchorTopPx(
    stop,
    stopsDesc,
    resolveStopLayoutOptions(textureType, options)
  );
}

/**
 * Top offset of the handle wrapper so the indicator line and dot centre sit on
 * the swatch vertical centre (same for gradient and stepped).
 */
export function stopHandleWrapperTopPx(
  stop: { id: string; value: string },
  stopsDesc: readonly { id: string; value: string }[],
  textureType: TextureType,
  options?: Partial<StopLayoutOptions>
): number {
  const anchorPx = stopBarMarkerAnchorTopPx(
    stop,
    stopsDesc,
    textureType,
    options
  );
  return anchorPx - GRADIENT_STOP_BAND_OFFSET_PX;
}

function stopBarColorAnchorTopPx(
  stop: { id: string; value: string },
  stopsDesc: readonly { id: string; value: string }[],
  layout: StopLayoutOptions
): number {
  const rowTopPx = stopValueLayoutRowTopPx(stop, stopsDesc, layout);
  return stopRowSwatchCenterTopPx(rowTopPx);
}

interface StopLayoutSteppedBand {
  readonly color: string;
  readonly topPx: number;
  readonly bottomPx: number;
}

/** Stepped bar bands between shared layout anchors (same px as gradient handles). */
function stopLayoutSteppedBandsPx(
  stopsDesc: readonly StopColorValue[],
  layout: StopLayoutOptions
): StopLayoutSteppedBand[] {
  const sortedAsc = stopsAscFromDesc(stopsDesc);
  const bottomStartPx = stopListHeightPx(stopsDesc.length) - STOP_ROW_HEIGHT_PX;
  const bandAnchorPx = sortedAsc.map((stop) =>
    stopBarColorAnchorTopPx(stop, stopsDesc, layout)
  );

  return sortedAsc.map((stop, index) => ({
    color: stop.color,
    topPx: bandAnchorPx[index],
    bottomPx: index === 0 ? bottomStartPx : bandAnchorPx[index - 1],
  }));
}

function formatBarStopPx(px: number): string {
  return `${px.toFixed(1)}px`;
}

function pushBarColorStop(parts: string[], color: string, px: number): void {
  parts.push(`${color} ${formatBarStopPx(px)}`);
}

/** Duplicate px stops so CSS does not blend distinct colours at a boundary. */
function pushBarHardEdge(
  parts: string[],
  fromColor: string,
  toColor: string,
  px: number
): void {
  pushBarColorStop(parts, fromColor, px);
  if (fromColor !== toColor) {
    pushBarColorStop(parts, toColor, px);
  }
}

/**
 * Full-height editor bar paint with pixel stops (overflow caps + middle section),
 * matching the UX prototype linear-gradient on a single track element.
 */
export function heatmapEditorBarPixelCss(
  stopsDesc: readonly StopColorValue[],
  textureType: TextureType,
  overflowOverrides?: OverflowColorOverrides,
  layoutOptions: Partial<StopLayoutOptions> = VIEWER_STOP_LAYOUT
): string {
  const editableStopCount = stopsDesc.length;
  if (editableStopCount === 0) {
    return OVERFLOW_GREY;
  }

  const totalHeightPx = stopListHeightPx(editableStopCount);
  const overflowPx = STOP_ROW_HEIGHT_PX;
  const bottomStartPx = totalHeightPx - overflowPx;
  const overflow = overflowStopColors(
    stopsDesc,
    overflowOverrides,
    textureType
  );
  const parts: string[] = [];

  const layout = resolveStopLayoutOptions(textureType, layoutOptions);

  if (textureType === 'stepped') {
    const bands = stopLayoutSteppedBandsPx(stopsDesc, layout).reverse();
    const topBandColor = bands[0]?.color ?? overflow.above;
    const topBandTopPx = bands[0]?.topPx ?? overflowPx;
    const bottomBandColor = bands.at(-1)?.color ?? overflow.below;

    pushBarColorStop(parts, overflow.above, 0);
    pushBarHardEdge(parts, overflow.above, topBandColor, topBandTopPx);

    for (const band of bands) {
      pushBarColorStop(parts, band.color, band.topPx);
      pushBarColorStop(parts, band.color, band.bottomPx);
    }

    pushBarHardEdge(parts, bottomBandColor, overflow.below, bottomStartPx);
    pushBarColorStop(parts, overflow.below, totalHeightPx);
  } else {
    const layoutStops = stopsDesc.map((stop) => ({
      color: stop.color,
      px: stopBarColorAnchorTopPx(stop, stopsDesc, layout),
    }));

    const firstAnchorPx = layoutStops[0]?.px ?? overflowPx;
    const lastAnchorPx = layoutStops.at(-1)?.px ?? bottomStartPx;
    const firstInRangeColor = layoutStops[0]?.color ?? overflow.above;
    const lastInRangeColor = layoutStops.at(-1)?.color ?? overflow.below;

    pushBarColorStop(parts, overflow.above, 0);
    pushBarHardEdge(parts, overflow.above, firstInRangeColor, firstAnchorPx);

    for (const stop of layoutStops) {
      pushBarColorStop(parts, stop.color, stop.px);
    }

    pushBarHardEdge(parts, lastInRangeColor, overflow.below, lastAnchorPx);
    pushBarColorStop(parts, overflow.below, totalHeightPx);
  }

  return `linear-gradient(${parts.join(', ')})`;
}

/** Bar background for the palette stop editor (handles + preview column). */
export function heatmapEditorBarCss(
  stopsDesc: readonly StopColorValue[],
  textureType: TextureType,
  overflowOverrides?: OverflowColorOverrides,
  layoutOptions: Partial<StopLayoutOptions> = VIEWER_STOP_LAYOUT
): string {
  return heatmapEditorBarPixelCss(
    stopsDesc,
    textureType,
    overflowOverrides,
    layoutOptions
  );
}

/**
 * Maps vertical layout percent (0 = top/high, 100 = bottom/low) to horizontal
 * preview percent (0 = left/low, 100 = right/high), reserving proportional
 * overflow caps at each end.
 */
export function verticalPercentToHorizontalPercent(
  verticalPercent: number
): number {
  const middleSpan =
    FULL_PERCENT - OVERFLOW_CAP_ROW_COUNT * OVERFLOW_CAP_WIDTH_PERCENT;
  return (
    OVERFLOW_CAP_WIDTH_PERCENT +
    ((FULL_PERCENT - verticalPercent) / FULL_PERCENT) * middleSpan
  );
}

function stopValueHorizontalPercent(
  stop: { value: string },
  stopsDesc: readonly { value: string }[]
): number {
  const range = stopValueRange(stopsDesc);
  return verticalPercentToHorizontalPercent(
    valueToVerticalPercent(parseStopValue(stop.value), range)
  );
}

function heatmapHorizontalGradientCss(
  stopsDesc: readonly StopColorValue[],
  overflow: { above: string; below: string }
): string {
  const capPercent = OVERFLOW_CAP_WIDTH_PERCENT;
  const middleEnd = FULL_PERCENT - capPercent;

  const layoutStops = stopsAscFromDesc(stopsDesc).map((stop) => ({
    color: stop.color,
    pct: stopValueHorizontalPercent(stop, stopsDesc),
  }));

  const firstInRangeColor = layoutStops[0]?.color ?? overflow.above;
  const lastInRangeColor = layoutStops.at(-1)?.color ?? overflow.below;

  const parts: string[] = [
    `${overflow.below} 0%`,
    `${overflow.below} ${capPercent.toFixed(1)}%`,
    `${firstInRangeColor} ${capPercent.toFixed(1)}%`,
    ...layoutStops.map((stop) => `${stop.color} ${stop.pct.toFixed(1)}%`),
    `${lastInRangeColor} ${middleEnd.toFixed(1)}%`,
    `${overflow.above} ${middleEnd.toFixed(1)}%`,
    `${overflow.above} ${FULL_PERCENT.toFixed(1)}%`,
  ];

  return `linear-gradient(to right, ${parts.join(', ')})`;
}

function heatmapHorizontalSteppedCss(
  stopsDesc: readonly StopColorValue[],
  overflow: { above: string; below: string }
): string {
  const capPercent = OVERFLOW_CAP_WIDTH_PERCENT;
  const middleEnd = FULL_PERCENT - capPercent;
  const sorted = stopsAscFromDesc(stopsDesc);
  const anchorPcts = sorted.map((stop) =>
    stopValueHorizontalPercent(stop, stopsDesc)
  );
  const topBandColor = sorted.at(-1)?.color ?? overflow.above;
  const topAnchorPct = anchorPcts.at(-1) ?? middleEnd;
  const bottomBandColor = sorted[0]?.color ?? overflow.below;

  const parts: string[] = [`${overflow.below} 0%`];

  pushBarHardEdgeHorizontal(parts, overflow.below, bottomBandColor, capPercent);

  for (let index = 0; index < sorted.length; index++) {
    const leftPct = index === 0 ? capPercent : anchorPcts[index - 1];
    const rightPct = anchorPcts[index];
    const stop = sorted[index];
    parts.push(
      `${stop.color} ${leftPct.toFixed(1)}%`,
      `${stop.color} ${rightPct.toFixed(1)}%`
    );
  }

  pushBarHardEdgeHorizontal(parts, topBandColor, overflow.above, topAnchorPct);
  parts.push(`${overflow.above} ${FULL_PERCENT.toFixed(1)}%`);

  return `linear-gradient(to right, ${parts.join(', ')})`;
}

function pushBarHardEdgeHorizontal(
  parts: string[],
  fromColor: string,
  toColor: string,
  pct: number
): void {
  parts.push(`${fromColor} ${pct.toFixed(1)}%`);
  if (fromColor !== toColor) {
    parts.push(`${toColor} ${pct.toFixed(1)}%`);
  }
}

/**
 * Value-proportional horizontal preview ramp with proportional overflow caps.
 * Gradient and stepped share the same stop positions; only bar paint differs.
 * @param stopsDesc Stops in value-descending order (canonical store order).
 */
export function heatmapHorizontalPreviewCss(
  stopsDesc: readonly StopColorValue[],
  textureType: TextureType,
  overflowOverrides?: OverflowColorOverrides
): string {
  if (stopsDesc.length === 0) {
    return OVERFLOW_GREY;
  }

  const overflow = overflowStopColors(
    stopsDesc,
    overflowOverrides,
    textureType
  );

  return textureType === 'stepped'
    ? heatmapHorizontalSteppedCss(stopsDesc, overflow)
    : heatmapHorizontalGradientCss(stopsDesc, overflow);
}
